import {
  History,
  MessageSquarePlus,
  Newspaper,
  StickyNote,
  Trash2,
} from "lucide-react";
import {
  useCreate,
  useDelete,
  useGetIdentity,
  useGetList,
  useGetMany,
  useLocaleState,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useId, useMemo, useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import {
  ExternalAnchor,
  SectionLabel,
} from "@/components/market-intel/components";
import { canDeleteUpdate, normalizeNote, sortTimeline } from "./competitors";
import { formatLongDate, toIsoDay } from "@/components/market-intel/dates";
import { FieldError } from "@/components/market-intel/FormDialog";
import { MiniMarkdown } from "@/components/market-intel/MiniMarkdown";
import { hostOf } from "@/components/market-intel/util";
import type { MarketCompetitorUpdate } from "@/components/market-intel/types";

interface SalesName {
  id: Identifier;
  first_name?: string | null;
  last_name?: string | null;
}

/**
 * The competitor's timeline, newest first: Flavia's news (newspaper icon,
 * source link) and the team's manual notes (tinted, with their author), plus
 * the "Añadir nota" composer. Authors can delete their own notes.
 */
export const CompetitorTimeline = ({
  competitorId,
}: {
  competitorId: Identifier;
}) => {
  const translate = useTranslate();
  const { data: updates } = useGetList<MarketCompetitorUpdate>(
    "market_competitor_updates",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "update_date", order: "DESC" },
      filter: { competitor_id: competitorId },
    },
  );
  const timeline = useMemo(() => sortTimeline(updates ?? []), [updates]);
  const authorIds = useMemo(
    () => [
      ...new Set(
        timeline
          .map((update) => update.sales_id)
          .filter((id): id is Identifier => id != null),
      ),
    ],
    [timeline],
  );
  const { data: authors } = useGetMany<SalesName>(
    "sales",
    { ids: authorIds },
    { enabled: authorIds.length > 0 },
  );
  const authorName = (id: Identifier | null | undefined) => {
    const author = authors?.find((sale) => String(sale.id) === String(id));
    return author
      ? [author.first_name, author.last_name].filter(Boolean).join(" ")
      : null;
  };

  return (
    <div className="space-y-3">
      <SectionLabel icon={History}>
        {translate("market.competitors.notes_timeline.heading")}
        <span className="ml-auto rounded-full bg-muted px-2 py-0.5 tracking-normal normal-case tabular-nums">
          {timeline.length}
        </span>
      </SectionLabel>
      <NoteComposer competitorId={competitorId} />
      {timeline.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("market.competitors.notes_timeline.empty")}
        </p>
      ) : (
        <ol className="mi-timeline space-y-5 pt-1">
          {timeline.map((update) => (
            <TimelineEntry
              key={update.id}
              update={update}
              author={authorName(update.sales_id)}
            />
          ))}
        </ol>
      )}
    </div>
  );
};

const TimelineEntry = ({
  update,
  author,
}: {
  update: MarketCompetitorUpdate;
  author: string | null;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const [remove, { isPending }] = useDelete<MarketCompetitorUpdate>();
  const [confirming, setConfirming] = useState(false);
  const isNote = update.kind === "nota";
  const Icon = isNote ? StickyNote : Newspaper;

  const onDelete = () => {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    remove(
      "market_competitor_updates",
      { id: update.id, previousData: update },
      {
        mutationMode: "optimistic",
        onSuccess: () =>
          notify("market.competitors.notes_timeline.deleted", { type: "info" }),
        onError: () =>
          notify("market.competitors.notes_timeline.error", { type: "error" }),
      },
    );
  };

  return (
    <li className="relative min-w-0" data-kind={isNote ? "nota" : "noticia"}>
      <span
        aria-hidden
        className="mi-timeline-dot"
        data-kind={isNote ? "nota" : undefined}
      />
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <span
          className={cn(
            "inline-flex h-5 items-center gap-1 rounded-full px-2 text-[11px] font-semibold",
            isNote
              ? "bg-amber-500/14 text-amber-800 dark:bg-amber-400/15 dark:text-amber-200"
              : "bg-primary/10 text-primary dark:bg-primary/15",
          )}
        >
          <Icon aria-hidden className="size-3" />
          {translate(
            isNote
              ? "market.competitors.notes_timeline.note"
              : "market.competitors.notes_timeline.news",
          )}
        </span>
        <time
          dateTime={update.update_date}
          className="text-[13px] font-semibold text-muted-foreground first-letter:uppercase"
        >
          {formatLongDate(update.update_date, locale)}
        </time>
      </div>
      <div
        className={cn(
          "mt-1.5",
          isNote &&
            "rounded-2xl bg-amber-500/8 px-3 py-2 ring-1 ring-amber-500/15 ring-inset dark:bg-amber-400/8 dark:ring-amber-400/15",
        )}
      >
        <MiniMarkdown source={update.summary} />
        {isNote ? (
          <div className="mt-1 flex min-w-0 items-center gap-2">
            <p className="min-w-0 truncate text-xs text-muted-foreground">
              {author
                ? translate("market.competitors.notes_timeline.by", {
                    name: author,
                  })
                : translate("market.competitors.notes_timeline.team_note")}
            </p>
            {canDeleteUpdate(update, identity?.id) ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onDelete}
                onBlur={() => setConfirming(false)}
                disabled={isPending}
                className={cn(
                  "-my-1.5 -mr-1.5 ml-auto h-8 shrink-0 rounded-full px-2.5 text-xs text-muted-foreground",
                  confirming && "text-destructive",
                )}
                aria-label={
                  confirming
                    ? translate(
                        "market.competitors.notes_timeline.delete_confirm",
                      )
                    : translate("market.competitors.notes_timeline.delete")
                }
              >
                <Trash2 aria-hidden className="size-3.5" />
                {confirming
                  ? translate(
                      "market.competitors.notes_timeline.delete_confirm",
                    )
                  : null}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {update.source_url ? (
          <ExternalAnchor href={update.source_url} className="mt-1 text-sm">
            {translate("market.competitors.source")} ·{" "}
            {hostOf(update.source_url)}
          </ExternalAnchor>
        ) : null}
      </div>
    </li>
  );
};

const NoteComposer = ({ competitorId }: { competitorId: Identifier }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [create, { isPending }] = useCreate<MarketCompetitorUpdate>();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const id = useId();

  if (!open)
    return (
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(true)}
        className="h-10 w-full justify-start rounded-full text-muted-foreground md:h-9"
      >
        <MessageSquarePlus aria-hidden className="size-4" />
        {translate("market.competitors.notes_timeline.add")}
      </Button>
    );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const result = normalizeNote(
      competitorId,
      text,
      toIsoDay(new Date()),
      identity?.id,
    );
    if (!result.ok) {
      setError(result.error);
      return;
    }
    create(
      "market_competitor_updates",
      { data: result.data },
      {
        onSuccess: () => {
          notify("market.competitors.notes_timeline.saved", {
            type: "success",
          });
          setText("");
          setOpen(false);
        },
        onError: () =>
          notify("market.competitors.notes_timeline.error", { type: "error" }),
      },
    );
  };

  return (
    <form onSubmit={submit} className="space-y-2" noValidate>
      <label htmlFor={`${id}-note`} className="sr-only">
        {translate("market.competitors.notes_timeline.add")}
      </label>
      <Textarea
        id={`${id}-note`}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setError(null);
        }}
        rows={3}
        autoFocus
        placeholder={translate("market.competitors.notes_timeline.placeholder")}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      {error ? (
        <FieldError id={`${id}-error`}>{translate(error)}</FieldError>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          className="rounded-full"
          onClick={() => {
            setOpen(false);
            setText("");
            setError(null);
          }}
        >
          {translate("market.competitors.notes_timeline.cancel")}
        </Button>
        <Button
          type="submit"
          disabled={isPending}
          className="btn-save rounded-full px-4"
        >
          {translate("market.competitors.notes_timeline.save")}
        </Button>
      </div>
    </form>
  );
};
